import { Spin } from "./Spin";
import { Alert } from "./Alert";
import React, { type CSSProperties, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined, ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { Button, type ButtonProps } from "./Button";
import { ModalBase, type ModalProps } from "./Dialog";
import { cx } from "./fieldContext";
import { useUiText } from "./uiText";
import { isAppRuntimeRecovering } from "../system/appRuntimeGate";
const MS_PER_SECOND = 1000;

/*
 * Rückmeldungen des Kits (Plan 2026-10-13-antd-ausbau, Stufe 5): kurze Meldungen (`message`),
 * Benachrichtigungen (`notification`), Rückfragen (`Modal.confirm` …) und `App` mit `App.useApp()`.
 * Ein gemeinsamer Speicher; `App` rendert die Ausgabe im Baum der App (Sprache, Theme). Ohne `App` hängt
 * sich beim ersten Aufruf eine eigene Ausgabe an das Dokument.
 */

type FeedbackType = "success" | "error" | "info" | "warning" | "loading";

const ICONS: Record<FeedbackType, ReactNode> = {
  success: <CheckCircleFilled />,
  error: <CloseCircleFilled />,
  info: <InfoCircleFilled />,
  warning: <ExclamationCircleFilled />,
  loading: <Spin inline />,
};

type MessageEntry = { id: number; key?: React.Key; type?: FeedbackType; content: ReactNode; icon?: ReactNode; duration: number; className?: string; style?: CSSProperties; onClose?: () => void; resolve: (value: boolean) => void };
type NotificationPlacement = "top" | "topLeft" | "topRight" | "bottom" | "bottomLeft" | "bottomRight";
type NotificationEntry = {
  id: number; key?: React.Key; type?: Exclude<FeedbackType, "loading">; title?: ReactNode; description?: ReactNode; icon?: ReactNode;
  actions?: ReactNode; duration: number; placement: NotificationPlacement; closable: boolean; className?: string; style?: CSSProperties;
  onClose?: () => void; onClick?: () => void;
};
type ConfirmEntry = { id: number; config: ConfirmConfig; open: boolean };

type FeedbackState = { messages: MessageEntry[]; notifications: NotificationEntry[]; confirms: ConfirmEntry[] };

let state: FeedbackState = { messages: [], notifications: [], confirms: [] };
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, number>();
let hostCount = 0;
let fallbackMounted = false;

const config = {
  message: { maxCount: undefined as number | undefined, duration: 3, top: 8 as number | string },
  notification: { placement: "topRight" as NotificationPlacement, duration: 4.5, maxCount: undefined as number | undefined },
};

function update(next: Partial<FeedbackState>) {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

function ensureHost() {
  if (hostCount > 0 || fallbackMounted || typeof document === "undefined") return;
  fallbackMounted = true;
  const node = document.createElement("div");
  node.className = "ck-feedback-fallback";
  document.body.appendChild(node);
  createRoot(node).render(<FeedbackHost fallback />);
}

function schedule(id: number, seconds: number, close: () => void) {
  if (!seconds || seconds <= 0) return;
  timers.set(id, window.setTimeout(close, seconds * MS_PER_SECOND));
}

function clearTimer(id: number) {
  const timer = timers.get(id);
  if (timer !== undefined) { window.clearTimeout(timer); timers.delete(id); }
}

// ---------------------------------------------------------------- message

export type MessageArgs = { content?: ReactNode; type?: FeedbackType; duration?: number; key?: React.Key; icon?: ReactNode; className?: string; style?: CSSProperties; onClose?: () => void };
/** Wie Antd: Aufruf schließt die Meldung, `then` wartet auf ihr Ende. */
export type MessageType = (() => void) & PromiseLike<boolean>;

function closeMessage(id: number) {
  const entry = state.messages.find((item) => item.id === id);
  if (!entry) return;
  clearTimer(id);
  update({ messages: state.messages.filter((item) => item.id !== id) });
  entry.onClose?.();
  entry.resolve(true);
}

function openMessage(args: MessageArgs): MessageType {
  if (args.type === "error" && isAppRuntimeRecovering()) {
    const closed = (() => undefined) as MessageType;
    closed.then = (onfulfilled, onrejected) => Promise.resolve(true).then(onfulfilled, onrejected);
    return closed;
  }
  ensureHost();
  let resolve: (value: boolean) => void = () => undefined;
  const promise = new Promise<boolean>((done) => { resolve = done; });
  const existing = args.key !== undefined ? state.messages.find((item) => item.key === args.key) : undefined;
  const id = existing?.id ?? nextId++;
  const entry: MessageEntry = {
    id, key: args.key, type: args.type, content: args.content, icon: args.icon, className: args.className, style: args.style, onClose: args.onClose,
    duration: args.duration ?? config.message.duration, resolve,
  };
  clearTimer(id);
  let messages = existing ? state.messages.map((item) => (item.id === id ? entry : item)) : [...state.messages, entry];
  const max = config.message.maxCount;
  if (max && messages.length > max) messages.slice(0, messages.length - max).forEach((item) => { clearTimer(item.id); item.resolve(true); });
  if (max && messages.length > max) messages = messages.slice(messages.length - max);
  update({ messages });
  schedule(id, entry.duration, () => closeMessage(id));
  const close = (() => closeMessage(id)) as MessageType;
  close.then = (onfulfilled, onrejected) => promise.then(onfulfilled, onrejected);
  return close;
}

type MessageContent = ReactNode | MessageArgs;
const isArgs = (value: MessageContent): value is MessageArgs =>
  typeof value === "object" && value !== null && !React.isValidElement(value) && !Array.isArray(value) && "content" in value;

function typed(type: FeedbackType) {
  return (content: MessageContent, duration?: number | (() => void), onClose?: () => void): MessageType => {
    const close = typeof duration === "function" ? duration : onClose;
    const seconds = typeof duration === "number" ? duration : undefined;
    return openMessage(isArgs(content) ? { ...content, type, duration: content.duration ?? seconds, onClose: content.onClose ?? close } : { content, type, duration: seconds, onClose: close });
  };
}

export type MessageInstance = {
  open: (args: MessageArgs) => MessageType;
  success: ReturnType<typeof typed>;
  error: ReturnType<typeof typed>;
  info: ReturnType<typeof typed>;
  warning: ReturnType<typeof typed>;
  loading: ReturnType<typeof typed>;
  destroy: (key?: React.Key) => void;
};

/** Kurze Meldungen oben in der Mitte (Ersatz für Antd `message`). */
export const message: MessageInstance & { config: (options: { maxCount?: number; duration?: number; top?: number | string }) => void; useMessage: () => [MessageInstance, ReactNode] } = {
  open: openMessage,
  success: typed("success"),
  error: typed("error"),
  info: typed("info"),
  warning: typed("warning"),
  loading: typed("loading"),
  destroy: (key) => {
    const targets = key === undefined ? state.messages : state.messages.filter((item) => item.key === key);
    targets.forEach((item) => closeMessage(item.id));
  },
  config: (options) => { Object.assign(config.message, Object.fromEntries(Object.entries(options).filter(([, value]) => value !== undefined))); },
  useMessage: () => [message, null],
};

// ---------------------------------------------------------------- notification

export type NotificationArgs = {
  /** Titel (Antd v6 `title`, früher `message`). */
  title?: ReactNode;
  message?: ReactNode;
  description?: ReactNode;
  type?: Exclude<FeedbackType, "loading">;
  icon?: ReactNode;
  /** Aktionen (Antd v6 `actions`, früher `btn`). */
  actions?: ReactNode;
  btn?: ReactNode;
  key?: React.Key;
  duration?: number | null | false;
  placement?: NotificationPlacement;
  closable?: boolean;
  closeIcon?: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClose?: () => void;
  onClick?: () => void;
  showProgress?: boolean;
  pauseOnHover?: boolean;
};

function closeNotification(id: number) {
  const entry = state.notifications.find((item) => item.id === id);
  if (!entry) return;
  clearTimer(id);
  update({ notifications: state.notifications.filter((item) => item.id !== id) });
  entry.onClose?.();
}

function openNotification(args: NotificationArgs) {
  if (args.type === "error" && isAppRuntimeRecovering()) return;
  ensureHost();
  const existing = args.key !== undefined ? state.notifications.find((item) => item.key === args.key) : undefined;
  const id = existing?.id ?? nextId++;
  const duration = args.duration === null || args.duration === false ? 0 : args.duration ?? config.notification.duration;
  const entry: NotificationEntry = {
    id, key: args.key, type: args.type, title: args.title ?? args.message, description: args.description, icon: args.icon,
    actions: args.actions ?? args.btn, duration, placement: args.placement ?? config.notification.placement, closable: args.closable !== false && args.closeIcon !== null,
    className: args.className, style: args.style, onClose: args.onClose, onClick: args.onClick,
  };
  clearTimer(id);
  let notifications = existing ? state.notifications.map((item) => (item.id === id ? entry : item)) : [...state.notifications, entry];
  const max = config.notification.maxCount;
  if (max && notifications.length > max) notifications = notifications.slice(notifications.length - max);
  update({ notifications });
  schedule(id, duration, () => closeNotification(id));
}

export type NotificationInstance = {
  open: (args: NotificationArgs) => void;
  success: (args: NotificationArgs) => void;
  error: (args: NotificationArgs) => void;
  info: (args: NotificationArgs) => void;
  warning: (args: NotificationArgs) => void;
  destroy: (key?: React.Key) => void;
};

/** Benachrichtigungen mit Titel und Beschreibung am Rand (Ersatz für Antd `notification`). */
export const notification: NotificationInstance & { config: (options: { placement?: NotificationPlacement; duration?: number; maxCount?: number }) => void; useNotification: () => [NotificationInstance, ReactNode] } = {
  open: openNotification,
  success: (args) => openNotification({ ...args, type: "success" }),
  error: (args) => openNotification({ ...args, type: "error" }),
  info: (args) => openNotification({ ...args, type: "info" }),
  warning: (args) => openNotification({ ...args, type: "warning" }),
  destroy: (key) => {
    const targets = key === undefined ? state.notifications : state.notifications.filter((item) => item.key === key);
    targets.forEach((item) => closeNotification(item.id));
  },
  config: (options) => { Object.assign(config.notification, Object.fromEntries(Object.entries(options).filter(([, value]) => value !== undefined))); },
  useNotification: () => [notification, null],
};

// ---------------------------------------------------------------- Rückfragen

export type ConfirmType = "confirm" | "info" | "success" | "error" | "warning";

export interface ModalFuncProps {
  type?: ConfirmType;
  title?: ReactNode;
  content?: ReactNode;
  icon?: ReactNode;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okType?: ModalProps["okType"];
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  okCancel?: boolean;
  /** Mit Parameter: der Aufrufer schließt selbst über die übergebene Funktion. */
  onOk?: ((close: () => void) => unknown) | (() => unknown);
  onCancel?: ((close: () => void) => unknown) | (() => unknown);
  afterClose?: () => void;
  width?: number | string;
  centered?: boolean;
  closable?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  autoFocusButton?: "ok" | "cancel" | null;
  footer?: ModalProps["footer"];
  className?: string;
  rootClassName?: string;
  wrapClassName?: string;
  zIndex?: number;
  style?: CSSProperties;
  styles?: ModalProps["styles"];
  /** Antd-Optionen ohne Wirkung im Kit. */
  focusable?: unknown;
  mask?: ModalProps["mask"];
  direction?: unknown;
}

type ConfirmConfig = ModalFuncProps & { resolve?: (value: boolean) => void };

export type ModalFuncReturn = {
  destroy: () => void;
  update: (next: ModalFuncProps | ((previous: ModalFuncProps) => ModalFuncProps)) => void;
  then: <T>(resolve: (confirmed: boolean) => T, reject?: (reason: unknown) => T) => Promise<T>;
};

function closeConfirm(id: number) {
  update({ confirms: state.confirms.map((item) => (item.id === id ? { ...item, open: false } : item)) });
}

function removeConfirm(id: number) {
  update({ confirms: state.confirms.filter((item) => item.id !== id) });
}

function openConfirm(type: ConfirmType, props: ModalFuncProps): ModalFuncReturn {
  ensureHost();
  const id = nextId++;
  let resolve: (value: boolean) => void = () => undefined;
  const promise = new Promise<boolean>((done) => { resolve = done; });
  update({ confirms: [...state.confirms, { id, open: true, config: { ...props, type: props.type ?? type, resolve } }] });
  return {
    destroy: () => closeConfirm(id),
    update: (next) => update({
      confirms: state.confirms.map((item) => (item.id === id ? { ...item, config: { ...item.config, ...(typeof next === "function" ? next(item.config) : next) } } : item)),
    }),
    then: (onfulfilled, onrejected) => promise.then(onfulfilled, onrejected),
  };
}

export type ModalStaticFunctions = {
  confirm: (props: ModalFuncProps) => ModalFuncReturn;
  info: (props: ModalFuncProps) => ModalFuncReturn;
  success: (props: ModalFuncProps) => ModalFuncReturn;
  error: (props: ModalFuncProps) => ModalFuncReturn;
  warning: (props: ModalFuncProps) => ModalFuncReturn;
  warn: (props: ModalFuncProps) => ModalFuncReturn;
};

export const modalApi: ModalStaticFunctions = {
  confirm: (props) => openConfirm("confirm", props),
  info: (props) => openConfirm("info", props),
  success: (props) => openConfirm("success", props),
  error: (props) => openConfirm("error", props),
  warning: (props) => openConfirm("warning", props),
  warn: (props) => openConfirm("warning", props),
};

export function destroyAllConfirms() {
  state.confirms.forEach((item) => closeConfirm(item.id));
}

const CONFIRM_ICON: Record<ConfirmType, ReactNode> = {
  confirm: <ExclamationCircleFilled />,
  warning: <ExclamationCircleFilled />,
  info: <InfoCircleFilled />,
  success: <CheckCircleFilled />,
  error: <CloseCircleFilled />,
};

function ConfirmDialog({ entry }: { entry: ConfirmEntry }) {
  const { text } = useUiText();
  const { config: props, id } = entry;
  const type = props.type ?? "confirm";
  const okCancel = props.okCancel ?? type === "confirm";
  const [busy, setBusy] = React.useState<"ok" | "cancel" | null>(null);
  const [failed, setFailed] = React.useState(false);
  const running = React.useRef(false);
  const settled = React.useRef(false);
  const okRef = React.useRef<HTMLButtonElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const finish = (confirmed: boolean) => {
    if (!settled.current) { settled.current = true; props.resolve?.(confirmed); }
    closeConfirm(id);
  };
  const run = async (action: ModalFuncProps["onOk"], which: "ok" | "cancel") => {
    if (running.current) return;
    setFailed(false);
    if (!action) { finish(which === "ok"); return; }
    running.current = true;
    let result: unknown;
    try { result = action.length > 0 ? (action as (close: () => void) => unknown)(() => finish(which === "ok")) : (action as () => unknown)(); }
    catch { running.current = false; setFailed(true); return; }
    if (result && typeof (result as Promise<unknown>).then === "function") {
      setBusy(which);
      try { await result; finish(which === "ok"); } catch { setFailed(true); } finally { running.current = false; setBusy(null); }
      return;
    }
    running.current = false;
    if (action.length === 0) finish(which === "ok");
  };

  React.useEffect(() => {
    if (!entry.open) return;
    const frame = window.requestAnimationFrame(() => {
      const target = props.autoFocusButton === null ? null : props.autoFocusButton === "cancel" ? cancelRef.current : okRef.current;
      target?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [entry.open, props.autoFocusButton]);

  const okButton = (
    <Button
      ref={okRef}
      type={props.okType === "default" ? "default" : "primary"}
      danger={props.okType === "danger"}
      loading={busy === "ok"}
      {...props.okButtonProps}
      onClick={() => run(props.onOk, "ok")}
    >
      {props.okText ?? text("OK")}
    </Button>
  );
  const cancelButton = okCancel ? (
    <Button ref={cancelRef} loading={busy === "cancel"} {...props.cancelButtonProps} onClick={() => run(props.onCancel, "cancel")}>
      {props.cancelText ?? text("Cancel")}
    </Button>
  ) : null;
  const icon = props.icon === null ? null : props.icon ?? CONFIRM_ICON[type];
  const buttons = <>{cancelButton}{okButton}</>;
  const footer = props.footer === undefined ? buttons : typeof props.footer === "function"
    ? props.footer(buttons, { OkBtn: () => okButton, CancelBtn: () => cancelButton })
    : props.footer;

  return (
    <ModalBase
      open={entry.open}
      confirmLoading={busy !== null}
      title={null}
      footer={null}
      closable={props.closable ?? false}
      maskClosable={props.maskClosable ?? false}
      keyboard={props.keyboard ?? true}
      centered={props.centered}
      width={props.width ?? 416}
      zIndex={props.zIndex}
      mask={props.mask}
      style={props.style}
      styles={props.styles}
      className={cx("ck-modal-confirm", `ck-modal-confirm-${type}`, props.className)}
      rootClassName={props.rootClassName}
      wrapClassName={props.wrapClassName}
      autoFocus={false}
      destroyOnHidden
      onCancel={() => void run(props.onCancel, "cancel")}
      afterClose={() => { props.afterClose?.(); removeConfirm(id); }}
    >
      <div className="ck-modal-confirm-body-wrapper">
        {failed && <Alert type="error" showIcon message={text("Action failed")} />}
        <div className={cx("ck-modal-confirm-body", !icon && "ck-modal-confirm-body-no-icon")}>
          {icon ? <span className="ck-modal-confirm-icon">{icon}</span> : null}
          <div className="ck-modal-confirm-paragraph">
            {props.title != null ? <span className="ck-modal-confirm-title">{props.title}</span> : null}
            {props.content != null ? <div className="ck-modal-confirm-content">{props.content}</div> : null}
          </div>
        </div>
        {footer != null && footer !== false ? <div className="ck-modal-confirm-btns">{footer}</div> : null}
      </div>
    </ModalBase>
  );
}

// ---------------------------------------------------------------- Ausgabe

function useFeedbackState() {
  return React.useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    () => state,
    () => state,
  );
}

const PLACEMENTS: NotificationPlacement[] = ["topRight", "topLeft", "top", "bottomRight", "bottomLeft", "bottom"];

function FeedbackHost({ fallback = false }: { fallback?: boolean }) {
  const { text } = useUiText();
  const current = useFeedbackState();
  const [owner, setOwner] = React.useState(fallback);
  React.useEffect(() => {
    if (fallback) return;
    hostCount += 1;
    setOwner(hostCount === 1);
    return () => { hostCount -= 1; };
  }, [fallback]);
  // Eine zweite `App` (z. B. in Tests verschachtelt) rendert nichts doppelt; ohne eigene `App` übernimmt die Rückfallausgabe.
  if (!owner || (fallback && hostCount > 0)) return null;
  return (
    <>
      {current.messages.length ? (
        <div className="ck-message" style={{ top: config.message.top }} role="status" aria-live="polite">
          {current.messages.map((item) => (
            <div key={item.id} className={cx("ck-message-notice-wrapper", item.className)} style={item.style}>
              <div className="ck-message-notice">
                <div className="ck-message-notice-content">
                  <div className={cx("ck-message-custom-content", item.type && `ck-message-${item.type}`)}>
                    {item.icon ?? (item.type ? <span className="ck-message-icon">{ICONS[item.type]}</span> : null)}
                    <span>{item.content}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
      {PLACEMENTS.map((placement) => {
        const list = current.notifications.filter((item) => item.placement === placement);
        if (!list.length) return null;
        return (
          <div key={placement} className={cx("ck-notification", `ck-notification-${placement}`)}>
            {list.map((item) => (
              <div key={item.id} className="ck-notification-notice-wrapper">
                <div
                  role="alert"
                  className={cx("ck-notification-notice", item.type && `ck-notification-notice-${item.type}`, item.closable && "ck-notification-notice-closable", item.className)}
                  style={item.style}
                  onClick={item.onClick}
                  onMouseEnter={() => clearTimer(item.id)}
                  onMouseLeave={() => schedule(item.id, item.duration, () => closeNotification(item.id))}
                >
                  {item.icon ?? (item.type ? <span className={cx("ck-notification-notice-icon", `ck-notification-notice-icon-${item.type}`)}>{ICONS[item.type]}</span> : null)}
                  <div className={cx("ck-notification-notice-section", Boolean(item.icon || item.type) && "has-icon")}>
                    {item.title != null ? <div className="ck-notification-notice-title">{item.title}</div> : null}
                    {item.description != null ? <div className="ck-notification-notice-description">{item.description}</div> : null}
                    {item.actions != null ? <div className="ck-notification-notice-actions">{item.actions}</div> : null}
                  </div>
                  {item.closable ? (
                    <button type="button" className="ck-notification-notice-close" aria-label={text("Close")} onClick={(event) => { event.stopPropagation(); closeNotification(item.id); }}>
                      <CloseOutlined />
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        );
      })}
      {current.confirms.map((entry) => <ConfirmDialog key={entry.id} entry={entry} />)}
    </>
  );
}

// ---------------------------------------------------------------- App

export type AppApi = { message: MessageInstance; notification: NotificationInstance; modal: ModalStaticFunctions };
const appApi: AppApi = { message, notification, modal: modalApi };

export interface AppProps {
  children?: ReactNode;
  className?: string;
  style?: CSSProperties;
  message?: { maxCount?: number; duration?: number; top?: number | string };
  notification?: { placement?: NotificationPlacement; duration?: number; maxCount?: number };
  /** Antd-Option: als `div` (Standard) oder ohne eigenes Element (`false`). */
  component?: false | "div" | "section";
}

/** Wurzel jeder App (Ersatz für Antd `App`): stellt `App.useApp()` bereit und zeigt Meldungen und Rückfragen an. */
function AppBase({ children, className, style, message: messageConfig, notification: notificationConfig, component = "div" }: AppProps) {
  React.useState(() => {
    if (messageConfig) message.config(messageConfig);
    if (notificationConfig) notification.config(notificationConfig);
    return null;
  });
  const content = <>{children}<FeedbackHost /></>;
  if (component === false) return content;
  const Element = component;
  return <Element className={cx("ck-app", className)} style={style}>{content}</Element>;
}

export const App = Object.assign(AppBase, { useApp: (): AppApi => appApi });

/** Dialog (Ersatz für Antd `Modal`) samt Rückfragen `Modal.confirm` … und `Modal.useModal()`. */
export const Modal = Object.assign(ModalBase, {
  ...modalApi,
  useModal: (): [ModalStaticFunctions, ReactNode] => [modalApi, null],
  destroyAll: destroyAllConfirms,
});
